import { Link, useLocation } from 'react-router-dom';
// mantine
import {
    Avatar,
    Menu,
    MenuDivider,
    Skeleton,
    Title,
    UnstyledButton,
    useMantineTheme
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
// components
import { Truncate } from '@newo/ui-kit';
import { ChevronDown, DollarSign, LogOut, Mail, Users } from 'lucide-react';
import { base64ToImageSource } from '@newo/toolkit/image';
import { SUPPORT_EMAIL } from '@newo/constants';
import MobileNavigation from '@/components/MobileNavigation';
// icons
// utils
import { useProfileQuery } from '@/services/customer/profile/profile.queries.ts';
import { PATHS } from '@/constants';
import useAppStore from '@/store/app';
import { cn } from '@/utils';
// assets
import styles from '@/components/Header/Header.module.css';
import BillingNotificationBanner from '@/components/BillingNotificationBanner';

interface HeaderProps {
    className?: string;
    title?: string;
}

const FIRST_LETTER_INDEX = 0;
const TRUNCATE_DESKTOP_LENGTH = 12;
const TRUNCATE_MOBILE_LENGTH = 8;

const handleSignOut = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    useAppStore.getState().logout();
};

const Header = ({ className, title = '' }: HeaderProps) => {
    const theme = useMantineTheme();
    const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm})`);
    const location = useLocation();
    const isCreatePartnerPage = location.pathname.includes(PATHS.createPartner);

    const { data: profile, isSuccess } = useProfileQuery(!isCreatePartnerPage);

    return (
        <div className={styles.wrapper}>
            <BillingNotificationBanner className={styles.alert} />
            <header className={cn(styles.header, 'text-base', className)}>
                <MobileNavigation className="block md:hidden" />
                <div className="flex items-start gap-2">
                    {isSuccess ? (
                        <div className="leading-none">
                            <Truncate
                                className="font-bold"
                                endLength={
                                    isDesktop
                                        ? TRUNCATE_DESKTOP_LENGTH
                                        : TRUNCATE_MOBILE_LENGTH
                                }
                                mode="middle"
                                startLength={
                                    isDesktop
                                        ? TRUNCATE_DESKTOP_LENGTH
                                        : TRUNCATE_MOBILE_LENGTH
                                }
                                text={profile.organization_name ?? ''}
                            />
                            <div className="text-xs/6 text-gray-6">
                                Idn: {profile.idn}
                            </div>
                        </div>
                    ) : (
                        <Skeleton height={10} mb={2} width={200} />
                    )}
                    <span className="text-gray-6 leading-none">/</span>
                    {typeof title === 'string' ? (
                        <Title
                            className="text-base leading-none"
                            fw={600}
                            order={1}
                        >
                            {title}
                        </Title>
                    ) : (
                        title
                    )}
                </div>
                <div className="ml-auto">
                    <Menu withArrow arrowPosition="side">
                        <Menu.Target>
                            <UnstyledButton className="hidden md:flex items-center gap-2">
                                <Avatar
                                    className="w-10 h-10 shrink-0 "
                                    color="blue"
                                    radius="xl"
                                    size="2rem"
                                    src={
                                        profile?.logo
                                            ? base64ToImageSource(profile.logo)
                                            : undefined
                                    }
                                >
                                    {(profile?.last_name ?? '')
                                        .at(FIRST_LETTER_INDEX)
                                        ?.toUpperCase()}
                                </Avatar>
                                Profile
                                <ChevronDown />
                            </UnstyledButton>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Item
                                component={Link}
                                leftSection={<DollarSign />}
                                to={PATHS.profile.billing}
                            >
                                Billing
                            </Menu.Item>
                            <Menu.Item
                                component={Link}
                                leftSection={<Users />}
                                to={PATHS.profile.members}
                            >
                                Members
                            </Menu.Item>
                            <Menu.Label>Email support</Menu.Label>
                            <Menu.Item
                                component={Link}
                                leftSection={<Mail />}
                                target="_blank"
                                to={`mailto:${SUPPORT_EMAIL}`}
                            >
                                {SUPPORT_EMAIL}
                            </Menu.Item>
                            <MenuDivider />
                            <Menu.Item
                                color="red"
                                leftSection={<LogOut />}
                                onClick={handleSignOut}
                            >
                                Logout
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </div>
            </header>
        </div>
    );
};

export default Header;
